import { createContext, useContext, useState, ReactNode } from "react";
import {
  SYSTEM_BASE_PRICES,
  DIMENSION_PRESETS,
  MATERIAL_MULTIPLIERS,
  type DimensionPreset,
} from "./pricingEngine";
import type { BathroomSystemType, BathroomMaterial } from "./types";

/* ═══════════════════════════════════════════════════
   Complexity adders shape
   ═══════════════════════════════════════════════════ */

export interface ComplexityAdders {
  standard: number;
  moderate: number;
  high: number;
  plumbingRelocation: number;
  customModifications: number;
}

/* ═══════════════════════════════════════════════════
   Full pricing config shape
   ═══════════════════════════════════════════════════ */

export interface PricingConfig {
  systemBasePrices: Record<BathroomSystemType, number>;
  dimensionPresets: DimensionPreset[];
  materialMultipliers: Record<BathroomMaterial, number>;
  complexityAdders: ComplexityAdders;
}

const DEFAULT_CONFIG: PricingConfig = {
  systemBasePrices: { ...SYSTEM_BASE_PRICES },
  dimensionPresets: DIMENSION_PRESETS.map((p) => ({ ...p })),
  materialMultipliers: { ...MATERIAL_MULTIPLIERS },
  complexityAdders: {
    standard: 0,
    moderate: 1200,
    high: 2500,
    plumbingRelocation: 1800,
    customModifications: 1200,
  },
};

interface PricingContextValue {
  config: PricingConfig;
  setConfig: React.Dispatch<React.SetStateAction<PricingConfig>>;
  resetToDefaults: () => void;
}

const PricingContext = createContext<PricingContextValue | undefined>(undefined);

export function PricingProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
  const resetToDefaults = () => setConfig(DEFAULT_CONFIG);

  return (
    <PricingContext.Provider value={{ config, setConfig, resetToDefaults }}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing() {
  const ctx = useContext(PricingContext);
  if (!ctx) throw new Error("usePricing must be used within PricingProvider");
  return ctx;
}
